import { InformationCriteriaStore } from 'stores';
import { mergeQueryString, QueryCombiner, updateSearch } from 'bernie-core';
import { ContextStore } from 'bernie-plugin-mobx';
import { Field } from 'uitk-react-form';
import {
  AddOnRequested,
  CarryOnBagType,
  CreditEligibleFilterType,
  FilterPolicyType,
  FlightsFilterType,
  FlightsSearchFilterValuesInput,
  FlightsStopFilterOperation,
  FlightsTimeOfDay,
  NoCancelFeeType,
  SeatChoiceType,
  SideSheetQueryContextInput
} from '__generated__/api';
import { SearchCriteriaStore } from 'stores';
import { RouteComponentProps } from 'react-router-dom';
import TriggerFlightsSearchParams from 'types/shared/trigger-flights-search-params';
import { FilterItem, updateSearchFilterValuesList } from 'experiments/PWA_USF_FSR';
import isEqual from 'lodash/isEqual';
import { closeDialogs } from 'utility';
import { compareFilterValues } from '../../stores/validators/filter-validator';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { ClientLogger } from 'bernie-client';

const mapFilterToAddonRequested = (value): AddOnRequested => {
  switch (value) {
    case FlightsFilterType.SEAT_CHOICE:
      return AddOnRequested.SC;
    case FlightsFilterType.CARRY_ON_BAG:
      return AddOnRequested.COB;
    case '1':
      return AddOnRequested.CB1;
    default:
      return;
  }
};

export const updateAddonRequestedList = (filterItem: FilterItem, addOnRequested: AddOnRequested[]) => {
  const { selected, value } = filterItem;
  const addOnValue = mapFilterToAddonRequested(value);

  if (addOnValue) {
    if (selected) {
      addOnRequested.push(addOnValue);
    } else {
      addOnRequested.splice(
        0,
        addOnRequested.length,
        ...addOnRequested.filter(currentAddon => currentAddon != addOnValue)
      );
    }
  }
};

export const onFilterPillSelectFIS = (
  field: Field,
  event: { target: { id: string; value: FlightsFilterType; checked: boolean } },
  informationCriteriaStore: InformationCriteriaStore
) => {
  const {
    target: { value, checked }
  } = event;
  const { informationFiltersValuesList, addOnRequested } = informationCriteriaStore.sideSheetTempValues;
  const fieldName = parseInt(value) === 1 ? FlightsFilterType.CHECKED_BAG : value;
  const filterItem = {
    id: field.name,
    selected: checked,
    name: fieldName,
    value: value
  };

  updateSearchFilterValuesList(filterItem, informationFiltersValuesList as FlightsSearchFilterValuesInput[]);
  updateAddonRequestedList(filterItem, addOnRequested);
};

export const updateUrlWithAddons = (
  history,
  location,
  pwaDialog?,
  upsellOfferToken?: string,
  addOnFilters?: string
) => {
  const queryCombiner: QueryCombiner = (prevQuery, newQuery) => newQuery;
  updateSearch({
    history,
    location,
    newParams: mergeQueryString(location.search, {
      pwaDialog: pwaDialog,
      upsellOfferToken: upsellOfferToken ?? undefined,
      addOnFilters: addOnFilters ?? undefined
    }),
    replace: true,
    queryCombiner
  });
};

export const mapAddonFromFilter = (filter: FlightsSearchFilterValuesInput): AddOnRequested => {
  const keys = Object.keys(filter);
  switch (keys[0]) {
    case 'seatChoiceBasedFilterValue':
      return AddOnRequested.SC;
    case 'carryOnBagBasedFilterValue':
      return AddOnRequested.COB;
    case 'checkedBagBasedFilterValue':
      return AddOnRequested.CB1;
    default:
      return;
  }
};

export const getAddonsRequested = (filters: FlightsSearchFilterValuesInput[]): AddOnRequested[] => {
  const addOnsRequested: AddOnRequested[] = [];
  filters.forEach(filter => {
    const addOn = mapAddonFromFilter(filter);
    if (addOn) {
      addOnsRequested.push(addOn);
    }
  });

  return addOnsRequested;
};

export const hasSameNumberOfFilters = (firstObject, secondObjects): boolean => {
  return (
    (firstObject as FlightsSearchFilterValuesInput[]).length ===
    (secondObjects as FlightsSearchFilterValuesInput[]).length
  );
};

export const areSameFiltersValuesList = (firstObject, secondObjects): boolean => {
  let hasSameFilters = true;

  (firstObject as FlightsSearchFilterValuesInput[]).forEach(filterValue => {
    const keyFirstObject = Object.keys(filterValue)[0];
    const keyValueFirstObject = Object.keys(filterValue[keyFirstObject])[0];
    const filterValueFirstObject = filterValue[keyFirstObject][keyValueFirstObject];
    const filterFound = compareFilterValues(keyFirstObject, keyValueFirstObject, filterValueFirstObject, secondObjects);

    if (!filterFound) {
      hasSameFilters = false;
    }
  });

  return hasSameFilters;
};

export const getFilterInput = (name: FlightsFilterType, value: string): FlightsSearchFilterValuesInput => {
  const {
    NUM_OF_STOPS,
    PREFERRED_AIRLINE,
    ARRIVAL_TIME,
    DEPARTURE_TIME,
    NO_CANCEL_FEE,
    CARRY_ON_BAG,
    CHECKED_BAG,
    SEAT_CHOICE,
    FLEXIBLE_CHANGE_POLICIES,
    CREDIT_ELIGIBLE,
    ARRIVAL_AIRPORT,
    DEPARTURE_AIRPORT
  } = FlightsFilterType;

  switch (name) {
    case NUM_OF_STOPS:
      return {
        numOfStopFilterValue: {
          stopInfo: {
            numberOfStops: parseInt(value),
            stopFilterOperation:
              parseInt(value) < 2 ? FlightsStopFilterOperation.EQUAL : FlightsStopFilterOperation.GREATER_THAN_EQUAL
          }
        }
      };
    case PREFERRED_AIRLINE:
      return {
        preferredAirlineFilterValue: {
          carrierCode: value
        }
      };
    case ARRIVAL_TIME:
      return {
        arrivalTimeFilterValue: {
          timeOfDay: value as FlightsTimeOfDay
        }
      };
    case DEPARTURE_TIME:
      return {
        departureTimeFilterValue: {
          timeOfDay: value as FlightsTimeOfDay
        }
      };
    case SEAT_CHOICE:
      return {
        seatChoiceBasedFilterValue: {
          seatChoice: value as SeatChoiceType
        }
      };
    case CARRY_ON_BAG:
      return {
        carryOnBagBasedFilterValue: {
          carryOnBag: value as CarryOnBagType
        }
      };
    case NO_CANCEL_FEE:
      return {
        noCancelFeeBasedFilterValue: {
          noCancelFee: value as NoCancelFeeType
        }
      };
    case FLEXIBLE_CHANGE_POLICIES:
      return {
        flexibleChangePolicyBasedFilterValue: {
          policyName: value as FilterPolicyType
        }
      };
    case CREDIT_ELIGIBLE:
      return {
        creditEligibleFilterValue: {
          creditEligible: CreditEligibleFilterType.CREDIT_ELIGIBLE
        }
      };
    case ARRIVAL_AIRPORT:
      return {
        arrivalAirportCodeFilterValue: {
          airportCode: value
        }
      };
    case DEPARTURE_AIRPORT:
      return {
        departureAirportCodeFilterValue: {
          airportCode: value
        }
      };
    case CHECKED_BAG:
      return {
        checkedBagBasedFilterValue: {
          numOfCheckedBags: parseInt(value)
        }
      };
    default:
      return;
  }
};

export const updateSearchFilterValues = (
  filterItem: FilterItem,
  searchFilterValuesList: FlightsSearchFilterValuesInput[]
) => {
  const { name, value, selected } = filterItem;
  const filterInput = getFilterInput(name, value);
  if (selected) {
    searchFilterValuesList.push(filterInput);
  } else {
    searchFilterValuesList.splice(
      0,
      searchFilterValuesList.length,
      ...searchFilterValuesList.filter(currentFilter => !isEqual(currentFilter, filterInput))
    );
  }
};

export const onAddOnPillSideSheetSelect = (
  field: Field,
  event: { target: { id: string; value: FlightsFilterType; checked: boolean } },
  searchCriteriaStore: SearchCriteriaStore
) => {
  const { addOnRequested, searchFilterValuesList } = searchCriteriaStore.dialogSideSheet;
  const {
    target: { value, checked }
  } = event;
  const fieldName = parseInt(value) === 1 ? FlightsFilterType.CHECKED_BAG : value;
  const filterItem = {
    id: field.name,
    selected: checked,
    name: fieldName,
    value: value
  };

  updateSearchFilterValues(filterItem, searchFilterValuesList as FlightsSearchFilterValuesInput[]);
  updateAddonRequestedList(filterItem, addOnRequested);
};

export const triggerSearchFromSideSheet = (
  searchCriteriaStore: SearchCriteriaStore,
  context: ContextStore,
  history: RouteComponentProps['history'],
  location: RouteComponentProps['location']
) => {
  const triggerSearchParams: TriggerFlightsSearchParams = {
    history,
    location,
    context,
    shouldForce: true,
    fetchFlexResults: false,
    fetchSearchForm: false
  };

  closeDialogs({
    history,
    location,
    callback: () => {
      searchCriteriaStore.dialogSideSheet.searchFilterValuesList = [];
      searchCriteriaStore.sideSheetQueryContext.journeyContinuationId = null;
      searchCriteriaStore.triggerSearch(triggerSearchParams);
    }
  });
};

export const getSideSheetQueryContextFromSearchResults = (
  searchFiltersValuesList: FlightsSearchFilterValuesInput[]
): SideSheetQueryContextInput => {
  const sideSheetQueryContextFeedback = Object.assign({}, SearchCriteriaStore.DEFAULTS.sideSheetQueryContext);
  Object.assign(sideSheetQueryContextFeedback, { addOnRequested: getAddonsRequested(searchFiltersValuesList) });

  return sideSheetQueryContextFeedback;
};

export const logEvent = (typeError: SystemEventLevel, logKey: string, description: string) => {
  const logger = ClientLogger.getLoggerWithIdentifier('SMART_SHOPPING');
  logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, logKey), description);
};
